import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../../api/client';
import { QuantitativeDataTable, Lifecycle5State, TableColumn } from '../../components/common/QuantitativeDataTable';
import { formatPrice, formatPercent, formatVolume } from '../../shared/domain/formatters';
import { compareDecimalStrings, getDecimalSign } from '../../shared/domain/decimal';
import { ASHARE_BILINGUAL } from '../../shared/constants/bilingualTitles';
import {
  Compass,
  TrendingUp,
  TrendingDown,
  Layers,
  RefreshCw,
  AlertCircle,
  SlidersHorizontal,
  ShieldAlert,
  ArrowUpRight,
  ArrowDownRight,
  Scale
} from 'lucide-react';

export interface IndexSummaryItem {
  index_code: string;
  index_name: string;
  trade_date: string;
  time: string;
  close: string;
  pre_close: string;
  change_points: string;
  pct_change: string;
  turnover: string;
  volume: string;
  session_kind?: string;
}

export interface IndexSummaryResponse {
  trade_date: string;
  readiness: string;
  session_kind?: string;
  indices: IndexSummaryItem[];
}

export interface AttributionItem {
  thscode: string;
  stock_name: string;
  weight: string;
  close: string;
  pct_change: string;
  point_contribution: string;
  contribution_ratio: string;
}

export interface IndexAttributionResponse {
  index_code: string;
  index_name: string;
  trade_date: string;
  time: string;
  index_close: string;
  index_change_points: string;
  index_pct_change: string;
  net_points_sum?: string;
  net_force_points?: string;
  sentiment_label: string;
  top_bullish: AttributionItem[];
  top_bearish: AttributionItem[];
  readiness: string;
}

/**
 * Pure Decimal-string weight percentage formatter.
 * Prohibits Number(...) and binary floating point operations (GB-06 Invariant).
 */
export function formatWeightPercent(rawWeight: unknown, decimals: 1 | 2 = 2): string {
  if (rawWeight === null || rawWeight === undefined || rawWeight === '' || rawWeight === '--') {
    return '--%';
  }
  const str = String(rawWeight).trim();
  if (!str || str === '--') return '--%';

  const [intPart = '0', rawFrac = ''] = str.split('.');
  const paddedFrac = rawFrac.padEnd(4, '0');

  const intBig = BigInt(intPart);
  const fracBig = BigInt(paddedFrac.slice(0, 4));
  const totalScaled = intBig * 10000n + fracBig;

  if (decimals === 1) {
    const scaled10 = totalScaled / 10n;
    const intVal = scaled10 / 10n;
    const fracVal = scaled10 % 10n;
    return `${intVal}.${fracVal}%`;
  }

  const intVal = totalScaled / 100n;
  const fracVal = totalScaled % 100n;
  return `${intVal}.${fracVal.toString().padStart(2, '0')}%`;
}

export interface ConstituentItem {
  thscode: string;
  stock_name: string;
  weight: string;
  close: string | null;
  pct_change: string | null;
  point_contribution: string | null;
  turnover: string | null;
}

export interface ConstituentsResponse {
  index_code: string;
  readiness: string;
  total_count: number;
  constituents: ConstituentItem[];
}

export interface AShareIndexRadarViewProps {
  colorMode?: 'mainland' | 'international';
  onToggleColorMode?: () => void;
}

const SUPPORTED_INDICES = [
  { code: '000016.SH', name: '上证50 (SSE 50)', shortName: '上证50', benchmark: '000001.SH' },
  { code: '399330.SZ', name: '深证100 (SZSE 100)', shortName: '深证100', benchmark: '399001.SZ' },
  { code: '000300.SH', name: '沪深300 (CSI 300)', shortName: '沪深300', benchmark: '000001.SH' },
];

export const AShareIndexRadarView: React.FC<AShareIndexRadarViewProps> = ({
  colorMode = 'mainland',
  onToggleColorMode,
}) => {
  const [selectedIndexCode, setSelectedIndexCode] = useState<string>('000016.SH');
  const [summaryData, setSummaryData] = useState<IndexSummaryItem[]>([]);
  const [attributionData, setAttributionData] = useState<IndexAttributionResponse | null>(null);
  const [constituents, setConstituents] = useState<ConstituentItem[]>([]);

  const [state, setState] = useState<Lifecycle5State>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string>('');
  const [sessionKind, setSessionKind] = useState<string>('');

  const getColor = useCallback((valStr: string) => {
    const sign = getDecimalSign(valStr);
    if (sign === 0) return 'var(--text-secondary)';
    if (colorMode === 'mainland') {
      return sign > 0 ? 'var(--color-bearish)' : 'var(--color-bullish)';
    }
    return sign > 0 ? 'var(--color-bullish)' : 'var(--color-bearish)';
  }, [colorMode]);

  const fetchData = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);

    try {
      const [sumRes, attrRes, constRes] = await Promise.all([
        apiClient.get<IndexSummaryResponse>('/api/v1/market/a-share/index/summary'),
        apiClient.get<IndexAttributionResponse>('/api/v1/market/a-share/index/attribution', {
          params: { index_code: selectedIndexCode }
        }),
        apiClient.get<ConstituentsResponse>('/api/v1/market/a-share/index/constituents', {
          params: { index_code: selectedIndexCode }
        }),
      ]);

      const indicesList = sumRes.indices || [];
      const sKind = sumRes.session_kind || (indicesList.length > 0 ? indicesList[0].session_kind : '') || '';
      setSessionKind(sKind);
      setSummaryData(indicesList);
      setAttributionData(attrRes);
      setConstituents(constRes.constituents || []);
      setLastRefreshedAt(new Date().toLocaleTimeString('zh-CN', { hour12: false }));

      // Resolve 5-state lifecycle
      if (indicesList.length === 0 && (!constRes.constituents || constRes.constituents.length === 0)) {
        setState('empty');
      } else if (sumRes.readiness === 'STALE' || attrRes.readiness === 'STALE') {
        setState('stale');
      } else {
        setState('fresh');
      }
    } catch (err: any) {
      setSummaryData([]);
      setAttributionData(null);
      setConstituents([]);
      setState('disconnected');
      setErrorMessage(err?.message || '无法连接指数与成份股后端服务');
    }
  }, [selectedIndexCode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Dual Compass Indices: SSE Composite (000001.SH) and SZSE Component (399001.SZ)
  const sseComposite = useMemo(() => {
    return summaryData.find((item) => item.index_code === '000001.SH');
  }, [summaryData]);

  const szseComponent = useMemo(() => {
    return summaryData.find((item) => item.index_code === '399001.SZ');
  }, [summaryData]);

  // Secondary Benchmark Badges
  const otherIndices = useMemo(() => {
    return summaryData.filter(
      (item) => item.index_code !== '000001.SH' && item.index_code !== '399001.SZ'
    );
  }, [summaryData]);

  // Market Breadth Calculation from constituents
  const breadthStats = useMemo(() => {
    if (!constituents || constituents.length === 0) {
      return { advancing: 0, declining: 0, flat: 0, advRatio: '0.0%', isHighResonance: false, isLowResonance: false };
    }
    let adv = 0;
    let dec = 0;
    let flat = 0;
    for (const c of constituents) {
      const s = getDecimalSign(c.pct_change);
      if (s > 0) adv++;
      else if (s < 0) dec++;
      else flat++;
    }
    const totalCount = BigInt(constituents.length);
    const ratioScaled = totalCount > 0n ? (BigInt(adv) * 1000n) / totalCount : 0n;
    const ratioInt = ratioScaled / 10n;
    const ratioFrac = ratioScaled % 10n;
    const ratioStr = `${ratioInt}.${ratioFrac}%`;

    const isHighResonance = BigInt(adv) * 100n > totalCount * 65n;
    const isLowResonance = BigInt(adv) * 100n < totalCount * 35n;

    return { advancing: adv, declining: dec, flat, advRatio: ratioStr, isHighResonance, isLowResonance };
  }, [constituents]);

  // Table columns for constituents
  const columns: TableColumn<ConstituentItem>[] = useMemo(() => [
    {
      key: 'thscode',
      title: '标的代码',
      width: '120px',
      sortable: true,
      sortValue: (r) => r.thscode,
      render: (r) => (
        <span style={{ fontWeight: 600, fontFamily: 'var(--font-mono, monospace)' }}>
          {r.thscode}
        </span>
      ),
    },
    {
      key: 'stock_name',
      title: '标的名称',
      width: '140px',
      sortable: true,
      sortValue: (r) => r.stock_name,
      render: (r) => (
        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
          {r.stock_name}
        </span>
      ),
    },
    {
      key: 'weight',
      title: '成份权重',
      align: 'right',
      width: '110px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.weight, b.weight),
      render: (r) => (
        <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-secondary)' }}>
          {formatWeightPercent(r.weight, 2)}
        </span>
      ),
    },
    {
      key: 'close',
      title: '最新价',
      align: 'right',
      width: '110px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.close, b.close),
      render: (r) => (
        <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600 }}>
          {r.close ? formatPrice(r.close) : '--'}
        </span>
      ),
    },
    {
      key: 'pct_change',
      title: '当日涨跌幅',
      align: 'right',
      width: '130px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.pct_change, b.pct_change),
      render: (r) => {
        if (!r.pct_change) {
          return <span style={{ color: 'var(--text-secondary)' }}>--</span>;
        }
        const color = getColor(r.pct_change);
        const sign = getDecimalSign(r.pct_change);
        const prefix = sign > 0 ? '+' : '';
        return (
          <span style={{ color, fontWeight: 700, fontFamily: 'var(--font-mono, monospace)' }}>
            {prefix}{formatPercent(r.pct_change)}
          </span>
        );
      },
    },
    {
      key: 'point_contribution',
      title: '拉盘/砸盘贡献点数',
      align: 'right',
      width: '160px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.point_contribution, b.point_contribution),
      render: (r) => {
        if (!r.point_contribution) {
          return <span style={{ color: 'var(--text-secondary)' }}>--</span>;
        }
        const color = getColor(r.point_contribution);
        const sign = getDecimalSign(r.point_contribution);
        const prefix = sign > 0 ? '+' : '';
        return (
          <span style={{
            color,
            fontWeight: 700,
            fontFamily: 'var(--font-mono, monospace)',
            padding: '2px 6px',
            borderRadius: '4px',
            background: color === 'var(--color-bearish)' ? 'var(--badge-bearish-bg)' : color === 'var(--color-bullish)' ? 'var(--badge-bullish-bg)' : 'transparent',
          }}>
            {prefix}{r.point_contribution} pts
          </span>
        );
      },
    },
    {
      key: 'turnover',
      title: '成交额',
      align: 'right',
      width: '120px',
      sortable: true,
      sortComparator: (a, b) => compareDecimalStrings(a.turnover, b.turnover),
      render: (r) => (
        <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-secondary)' }}>
          {r.turnover ? formatVolume(r.turnover, { abbreviate: 'zh' }) : '--'}
        </span>
      ),
    },
  ], [getColor]);

  return (
    <div className="view-container animate-fade-in" style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* View Header with Control Bar */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <Compass size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">{ASHARE_BILINGUAL.indexRadar.zh}</h2>
            {(sessionKind === 'DAY_CLOSE' || sessionKind === 'CLOSED') && (
              <span style={{
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '4px',
                background: 'var(--badge-info-bg)',
                color: 'var(--accent-cyan)',
                fontWeight: 600,
                border: '1px solid var(--border-glass)',
              }}>
                ● 盘后收盘 ({sessionKind === 'CLOSED' ? 'CLOSED' : 'DAY_CLOSE'} · 数据已封盘) {lastRefreshedAt ? `(${lastRefreshedAt})` : ''}
              </span>
            )}
            {sessionKind !== 'DAY_CLOSE' && sessionKind !== 'CLOSED' && state === 'fresh' && (
              <span style={{
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '4px',
                background: 'var(--badge-bullish-bg)',
                color: 'var(--color-bullish)',
                fontWeight: 600,
                border: '1px solid var(--badge-bullish-border)'
              }}>
                ● 实时就绪 (FRESH) {lastRefreshedAt ? `(${lastRefreshedAt})` : ''}
              </span>
            )}
            {sessionKind !== 'DAY_CLOSE' && sessionKind !== 'CLOSED' && state === 'stale' && (
              <span style={{
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '4px',
                background: 'var(--badge-warning-bg)',
                color: 'var(--color-warning)',
                fontWeight: 600,
                border: '1px solid var(--badge-warning-border)'
              }}>
                ▲ 数据时效迟延 (STALE &gt; 180s)
              </span>
            )}
          </div>
          <span className="bilingual-header-subtitle-en">{ASHARE_BILINGUAL.indexRadar.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            基于权重拉动分解模型，实时解构上证/深证大盘成份股拉盘与砸盘点数，支撑仓位与大盘联动研判
          </p>
        </div>

        {/* Global Controls */}
        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onToggleColorMode && (
            <button
              onClick={onToggleColorMode}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                fontSize: '11px',
                borderRadius: '6px',
                border: '1px solid var(--border-subtle)',
                background: 'var(--bg-surface)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
              title="切换涨跌色彩习惯"
            >
              <SlidersHorizontal size={12} />
              <span>{colorMode === 'mainland' ? '红涨绿跌 (国内)' : '绿涨红跌 (国际)'}</span>
            </button>
          )}

          <button
            onClick={fetchData}
            disabled={state === 'loading'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: 600,
              borderRadius: '6px',
              border: '1px solid var(--border-focus)',
              background: 'var(--badge-info-bg)',
              color: 'var(--accent-cyan)',
              cursor: state === 'loading' ? 'not-allowed' : 'pointer',
            }}
          >
            <RefreshCw size={12} className={state === 'loading' ? 'spin-animation' : ''} />
            <span>刷新</span>
          </button>
        </div>
      </div>

      {/* Disconnected or Empty State Alerts */}
      {state === 'disconnected' && (
        <div style={{
          padding: '16px 20px',
          borderRadius: '8px',
          background: 'var(--alert-bearish-bg)',
          border: '1px solid var(--alert-bearish-border)',
          color: 'var(--color-bearish)',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AlertCircle size={20} />
            <div>
              <div style={{ fontWeight: 600 }}>数据库链路断开或异常 (DISCONNECTED)</div>
              <div style={{ fontSize: '0.82rem', opacity: 0.9 }}>{errorMessage}</div>
            </div>
          </div>
          <button
            onClick={fetchData}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              background: 'var(--color-bearish)',
              color: 'var(--text-inverse)',
              border: 'none',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            重试连接
          </button>
        </div>
      )}

      {/* Module 1: Dual Compass (SSE vs SZSE + Broad Universe Badges) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '16px',
        marginBottom: '20px',
      }}>
        {/* SSE Composite Card */}
        <div style={{
          background: 'var(--bg-card)',
          borderRadius: '10px',
          padding: '16px 20px',
          border: '1px solid var(--border-card)',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                上证综合指数 · 000001.SH
              </span>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '4px 0 0 0', color: 'var(--text-primary)' }}>
                上证指数
              </h2>
            </div>
            {sseComposite && (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: getColor(sseComposite.pct_change),
                fontWeight: 700,
                fontSize: '0.95rem',
              }}>
                {getDecimalSign(sseComposite.pct_change) >= 0 ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
                {formatPercent(sseComposite.pct_change)}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', marginTop: '12px' }}>
            <span style={{
              fontSize: '2rem',
              fontWeight: 800,
              fontFamily: 'var(--font-mono, monospace)',
              color: sseComposite ? getColor(sseComposite.pct_change) : 'var(--text-primary)'
            }}>
              {sseComposite ? formatPrice(sseComposite.close) : '--'}
            </span>
            <span style={{
              fontSize: '1rem',
              fontWeight: 600,
              fontFamily: 'var(--font-mono, monospace)',
              color: sseComposite ? getColor(sseComposite.change_points) : 'var(--text-secondary)'
            }}>
              {sseComposite ? `${getDecimalSign(sseComposite.change_points) > 0 ? '+' : ''}${sseComposite.change_points} pts` : '--'}
            </span>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '14px',
            paddingTop: '10px',
            borderTop: '1px solid var(--border-subtle)',
            fontSize: '0.78rem',
            color: 'var(--text-secondary)'
          }}>
            <span>成交额: {sseComposite ? formatVolume(sseComposite.turnover, { abbreviate: 'zh' }) : '--'}</span>
            <span>更新: {sseComposite ? sseComposite.time.slice(11, 19) : '--'}</span>
          </div>
        </div>

        {/* SZSE Component Card */}
        <div style={{
          background: 'var(--bg-card)',
          borderRadius: '10px',
          padding: '16px 20px',
          border: '1px solid var(--border-card)',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                深证成份指数 · 399001.SZ
              </span>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '4px 0 0 0', color: 'var(--text-primary)' }}>
                深证成指
              </h2>
            </div>
            {szseComponent && (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: getColor(szseComponent.pct_change),
                fontWeight: 700,
                fontSize: '0.95rem',
              }}>
                {getDecimalSign(szseComponent.pct_change) >= 0 ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
                {formatPercent(szseComponent.pct_change)}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', marginTop: '12px' }}>
            <span style={{
              fontSize: '2rem',
              fontWeight: 800,
              fontFamily: 'var(--font-mono, monospace)',
              color: szseComponent ? getColor(szseComponent.pct_change) : 'var(--text-primary)'
            }}>
              {szseComponent ? formatPrice(szseComponent.close) : '--'}
            </span>
            <span style={{
              fontSize: '1rem',
              fontWeight: 600,
              fontFamily: 'var(--font-mono, monospace)',
              color: szseComponent ? getColor(szseComponent.change_points) : 'var(--text-secondary)'
            }}>
              {szseComponent ? `${getDecimalSign(szseComponent.change_points) > 0 ? '+' : ''}${szseComponent.change_points} pts` : '--'}
            </span>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '14px',
            paddingTop: '10px',
            borderTop: '1px solid var(--border-subtle)',
            fontSize: '0.78rem',
            color: 'var(--text-secondary)'
          }}>
            <span>成交额: {szseComponent ? formatVolume(szseComponent.turnover, { abbreviate: 'zh' }) : '--'}</span>
            <span>更新: {szseComponent ? szseComponent.time.slice(11, 19) : '--'}</span>
          </div>
        </div>

        {/* Additional Benchmark Badges Matrix */}
        <div style={{
          background: 'var(--bg-card)',
          borderRadius: '10px',
          padding: '16px 20px',
          border: '1px solid var(--border-card)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          boxShadow: 'var(--shadow-card)',
        }}>
          <div>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
              核心宽基与特色指数走廊
            </span>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '10px',
              marginTop: '10px'
            }}>
              {otherIndices.map((idx) => {
                const isSelected = idx.index_code === selectedIndexCode;
                const color = getColor(idx.pct_change);
                return (
                  <div
                    key={idx.index_code}
                    onClick={() => {
                      if (SUPPORTED_INDICES.some(s => s.code === idx.index_code)) {
                        setSelectedIndexCode(idx.index_code);
                      }
                    }}
                    style={{
                      padding: '8px 10px',
                      borderRadius: '6px',
                      background: isSelected ? 'var(--badge-info-bg)' : 'var(--tag-inactive-bg)',
                      border: isSelected ? '1px solid var(--border-focus)' : '1px solid var(--border-subtle)',
                      cursor: SUPPORTED_INDICES.some(s => s.code === idx.index_code) ? 'pointer' : 'default',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {idx.index_name}
                      </span>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color, fontFamily: 'var(--font-mono, monospace)' }}>
                        {formatPercent(idx.pct_change)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{formatPrice(idx.close)}</span>
                      <span>{formatVolume(idx.turnover, { abbreviate: 'zh' })}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '12px',
            fontSize: '0.75rem',
            color: 'var(--text-secondary)'
          }}>
            <span>成份股涨跌比: <strong style={{ color: colorMode === 'mainland' ? 'var(--color-bearish)' : 'var(--color-bullish)' }}>{breadthStats.advancing} 涨</strong> / <strong style={{ color: colorMode === 'mainland' ? 'var(--color-bullish)' : 'var(--color-bearish)' }}>{breadthStats.declining} 跌</strong></span>
            <span>广度共振: <strong style={{ color: 'var(--text-primary)' }}>{breadthStats.advRatio}</strong></span>
          </div>
        </div>
      </div>

      {/* Benchmark Index Switcher Tabs */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '16px',
        borderBottom: '1px solid var(--border-subtle)',
        paddingBottom: '10px'
      }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginRight: '8px' }}>
          归因指数切换:
        </span>
        {SUPPORTED_INDICES.map((item) => {
          const active = item.code === selectedIndexCode;
          return (
            <button
              key={item.code}
              onClick={() => setSelectedIndexCode(item.code)}
              style={{
                padding: '6px 14px',
                fontSize: '0.82rem',
                fontWeight: active ? 700 : 500,
                borderRadius: '6px',
                border: active ? '1px solid var(--border-focus)' : '1px solid transparent',
                background: active ? 'var(--badge-info-bg)' : 'transparent',
                color: active ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {item.name}
            </button>
          );
        })}
      </div>

      {/* Module 2: Point Contribution Heatmap & Attribution Breakdown */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
        gap: '16px',
        marginBottom: '20px',
      }}>
        {/* Top 10 Bullish Drivers */}
        <div style={{
          background: 'var(--bg-card)',
          borderRadius: '10px',
          padding: '16px 20px',
          border: '1px solid var(--border-card)',
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={18} color="var(--color-bearish)" />
              <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                领涨护盘 · Top 10 点数正贡献
              </h3>
            </div>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              拉动指数上行动力
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(attributionData?.top_bullish || []).slice(0, 8).map((item, idx) => {
              const color = getColor(item.point_contribution);
              return (
                <div
                  key={item.thscode}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    background: 'var(--tag-inactive-bg)',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize: '0.72rem',
                      width: '18px',
                      textAlign: 'center',
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-mono, monospace)',
                    }}>
                      {idx + 1}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                      {item.stock_name}
                    </span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono, monospace)' }}>
                      {item.thscode}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                      权重 {formatWeightPercent(item.weight, 1)}
                    </span>
                    <span style={{
                      fontSize: '0.78rem',
                      color: getColor(item.pct_change),
                      fontWeight: 600,
                      fontFamily: 'var(--font-mono, monospace)'
                    }}>
                      {formatPercent(item.pct_change)}
                    </span>
                    <span style={{
                      fontWeight: 700,
                      color,
                      fontFamily: 'var(--font-mono, monospace)',
                      fontSize: '0.85rem',
                      minWidth: '75px',
                      textAlign: 'right'
                    }}>
                      +{item.point_contribution} pts
                    </span>
                  </div>
                </div>
              );
            })}
            {(!attributionData?.top_bullish || attributionData.top_bullish.length === 0) && (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                暂无领涨成份股点数数据
              </div>
            )}
          </div>
        </div>

        {/* Top 10 Bearish Draggers */}
        <div style={{
          background: 'var(--bg-card)',
          borderRadius: '10px',
          padding: '16px 20px',
          border: '1px solid var(--border-card)',
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingDown size={18} color="var(--color-bullish)" />
              <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                领跌拖累 · Top 10 点数负贡献
              </h3>
            </div>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              打压指数拖累点数
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(attributionData?.top_bearish || []).slice(0, 8).map((item, idx) => {
              const color = getColor(item.point_contribution);
              return (
                <div
                  key={item.thscode}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    background: 'var(--tag-inactive-bg)',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize: '0.72rem',
                      width: '18px',
                      textAlign: 'center',
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-mono, monospace)',
                    }}>
                      {idx + 1}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                      {item.stock_name}
                    </span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono, monospace)' }}>
                      {item.thscode}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                      权重 {formatWeightPercent(item.weight, 1)}
                    </span>
                    <span style={{
                      fontSize: '0.78rem',
                      color: getColor(item.pct_change),
                      fontWeight: 600,
                      fontFamily: 'var(--font-mono, monospace)'
                    }}>
                      {formatPercent(item.pct_change)}
                    </span>
                    <span style={{
                      fontWeight: 700,
                      color,
                      fontFamily: 'var(--font-mono, monospace)',
                      fontSize: '0.85rem',
                      minWidth: '75px',
                      textAlign: 'right'
                    }}>
                      {item.point_contribution} pts
                    </span>
                  </div>
                </div>
              );
            })}
            {(!attributionData?.top_bearish || attributionData.top_bearish.length === 0) && (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                暂无领跌成份股点数数据
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Module 3: Full Constituents Table */}
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: '10px',
        padding: '16px 20px',
        border: '1px solid var(--border-card)',
        marginBottom: '20px',
        boxShadow: 'var(--shadow-card)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Layers size={18} color="var(--accent-cyan)" />
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
              成份股点数归因明细表 ({constituents.length} 只标的)
            </h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.82rem' }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              净合力冲击: <strong style={{
                color: (attributionData?.net_points_sum || attributionData?.net_force_points) ? getColor(attributionData?.net_points_sum || attributionData?.net_force_points || '0') : 'var(--text-secondary)',
                fontFamily: 'var(--font-mono, monospace)'
              }}>
                {(() => {
                  const pts = attributionData?.net_points_sum || attributionData?.net_force_points;
                  if (!pts) return '--';
                  const sign = getDecimalSign(pts) > 0 ? '+' : '';
                  return `${sign}${pts} pts`;
                })()}
              </strong>
            </span>
            <span style={{
              padding: '2px 8px',
              borderRadius: '4px',
              background: 'var(--badge-info-bg)',
              border: '1px solid var(--border-glass)',
              color: 'var(--accent-cyan)',
              fontWeight: 600,
            }}>
              情绪态势: {attributionData?.sentiment_label || 'NORMAL'}
            </span>
          </div>
        </div>

        <QuantitativeDataTable<ConstituentItem>
          data={constituents}
          columns={columns}
          state={state}
          errorMessage={errorMessage}
          onRetry={fetchData}
          rowKey={(r) => r.thscode}
          pageSize={30}
          emptyMessage="当前指数成份股数据暂未入库或离线"
          searchPlaceholder="按股票代码或名称过滤成份股..."
          searchFilter={(r, query) =>
            r.thscode.toLowerCase().includes(query.toLowerCase()) ||
            r.stock_name.toLowerCase().includes(query.toLowerCase())
          }
          initialSortKey="point_contribution"
          initialSortDirection="desc"
        />
      </div>

      {/* Module 4: Descriptive Market-Holding Linkage Telemetry (联动研判走廊) */}
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: '10px',
        padding: '18px 22px',
        border: '1px solid var(--border-card)',
        boxShadow: 'var(--shadow-card)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <Scale size={18} color="var(--accent-cyan)" />
          <div className="bilingual-card-title">
            <h3 className="bilingual-card-title-zh" style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
              个股持仓与大盘联动量化研判
            </h3>
            <span className="bilingual-card-subtitle-en">MARKET LINKAGE CORRIDOR</span>
          </div>
          <span style={{
            fontSize: '0.72rem',
            padding: '2px 8px',
            borderRadius: '4px',
            background: 'var(--badge-info-bg)',
            color: 'var(--accent-cyan)',
            fontWeight: 600,
            border: '1px solid var(--border-glass)',
          }}>
            量化研判参考 · 严禁交易决策指令
          </span>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '14px',
        }}>
          {/* Card 1: 成份股共振度 */}
          <div style={{
            padding: '14px 16px',
            borderRadius: '8px',
            background: 'var(--bg-canvas)',
            border: '1px solid var(--border-subtle)',
            boxShadow: 'var(--shadow-card)',
            transition: 'all 0.15s ease',
          }}>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.2px' }}>
              成份股共振度 (Breadth Resonance)
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '6px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
              {breadthStats.advRatio} <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>上涨</span>
            </div>
            <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', margin: '8px 0 0 0', lineHeight: 1.5 }}>
              {breadthStats.isHighResonance ? '成份股普涨共振，指数突破具备强广度支撑。' :
               breadthStats.isLowResonance ? '成份股普遍走弱，下行压力由多数标的共振产生。' :
               '板块分化明显，指数呈现存量博弈拉锯结构。'}
            </p>
          </div>

          {/* Card 2: 权重主导度 */}
          <div style={{
            padding: '14px 16px',
            borderRadius: '8px',
            background: 'var(--bg-canvas)',
            border: '1px solid var(--border-subtle)',
            boxShadow: 'var(--shadow-card)',
            transition: 'all 0.15s ease',
          }}>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.2px' }}>
              权重主导度 (Weight Concentration Ratio)
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '6px', color: 'var(--accent-cyan)' }}>
              Top 3 贡献占比
            </div>
            <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', margin: '8px 0 0 0', lineHeight: 1.5 }}>
              若 Top 3 标的拉动点数占比超过 50%，需警惕“拉抬权重掩护个股出货”的失真行情。
            </p>
          </div>

          {/* Card 3: 持仓贝塔联动提示 */}
          <div style={{
            padding: '14px 16px',
            borderRadius: '8px',
            background: 'var(--bg-canvas)',
            border: '1px solid var(--border-subtle)',
            boxShadow: 'var(--shadow-card)',
            transition: 'all 0.15s ease',
          }}>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.2px' }}>
              持仓贝塔联动提示 (Portfolio Beta Context)
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '6px', color: 'var(--color-bullish)' }}>
              联动观察窗
            </div>
            <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', margin: '8px 0 0 0', lineHeight: 1.5 }}>
              当指数大幅异动而持仓个股逆势滞涨时，应重点排查个股基本面与行业轮动节奏差异。
            </p>
          </div>
        </div>

        <div style={{
          marginTop: '14px',
          paddingTop: '12px',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.74rem',
          color: 'var(--text-muted)',
          lineHeight: 1.4,
        }}>
          <ShieldAlert size={14} color="var(--text-muted)" style={{ flexShrink: 0 }} />
          <span>
            系统声明：本雷达展示之指数点数贡献与联动提示纯属量化归因描述性事实，不构成任何买卖指令、投资建议或自动化信号（Hard-Lock GB-06）。
          </span>
        </div>
      </div>
    </div>
  );
};
